/**
 * The card that explains a non-working-hours wash: "Outside of working hours".
 *
 * Presentational only — it is told where to sit. The two callers differ in how
 * they DETECT the wash (a wrapper in the Schedule-All wizard, a hook attached to
 * the shipped calendar's existing root) but draw the same thing, so the drawing
 * lives here and the detection stays with each.
 *
 * `position: fixed` + portalling is the caller's job, not this component's: both
 * calendars mount inside a transformed modal wrapper, where a fixed box resolves
 * against that wrapper rather than the viewport.
 */
import { Box, Flex } from '@chakra-ui/react';
import { Clock } from '@kula-ai/icons-core';

export const UNAVAILABLE_HOURS_TITLE = 'Outside of working hours';

/** Sized so the caller can flip the card off a viewport edge before painting. */
export const UNAVAILABLE_HOURS_CARD_WIDTH_PX = 280;
export const UNAVAILABLE_HOURS_CARD_HEIGHT_PX = 72;

export type UnavailableHoursHoverCardProps = {
  top: number;
  left: number;
  title?: string;
  /** Optional second line — the card reads fine on the title alone. */
  description?: string;
};

export function UnavailableHoursHoverCard({
  top,
  left,
  title = UNAVAILABLE_HOURS_TITLE,
  description,
}: UnavailableHoursHoverCardProps) {
  return (
    <Box
      role="tooltip"
      position="fixed"
      // Inline, not Chakra props: these change on every mousemove, and Chakra
      // would compile each pixel value into its own emotion class.
      style={{ top: `${top}px`, left: `${left}px` }}
      w={`${UNAVAILABLE_HOURS_CARD_WIDTH_PX}px`}
      bg="white"
      borderWidth="1px"
      borderColor="secondary-100"
      borderRadius="12px"
      boxShadow="0px 4px 16px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.04)"
      // Never under the pointer: the card would otherwise take the mousemove
      // that keeps it alive and flicker itself out of existence.
      pointerEvents="none"
      zIndex="tooltip"
      p="3.5"
    >
      {/* Centred on each other: the card usually carries a single line, and
          top-aligning a 20px line against a 30px tile left the text riding
          high. With a second line the pair still centres as a block. */}
      <Flex gap="2.5" align="center">
        <Flex
          bg="pastel-4-light"
          borderRadius="6px"
          boxSize="30px"
          align="center"
          justify="center"
          flexShrink={0}
        >
          <Clock size={18} color="var(--kula-colors-pastel-4-dark)" />
        </Flex>
        <Flex direction="column" gap="1" flex="1" minW={0}>
          <Box
            fontSize="14px"
            lineHeight="20px"
            fontWeight={550}
            color="secondary-900"
          >
            {title}
          </Box>
          {description && (
            <Box fontSize="14px" lineHeight="16px" color="secondary-600">
              {description}
            </Box>
          )}
        </Flex>
      </Flex>
    </Box>
  );
}
